import React from 'react';

/** SRS #20 — one legend vocabulary for the whole product.
 *
 * Every colour-coded surface (map, status badges, status charts) draws its
 * colours and labels from here, and renders them with the same <Legend>, so
 * "green" means the same thing on the map page, the regulations table and
 * the analytics pie. */

// ── Regulation status ──────────────────────────────────────────────────────────
export type StatusTone = 'in_force' | 'pending' | 'inactive' | 'unknown';
export const STATUS_TONES: Record<StatusTone, { color: string; label: string; hint: string }> = {
  in_force: { color: '#12b76a', label: 'In force', hint: 'Enacted, passed, signed, effective or approved' },
  pending:  { color: '#f79009', label: 'Proposed / pending', hint: 'Draft, introduced, under review or consideration' },
  inactive: { color: '#f04438', label: 'No longer in force', hint: 'Repealed, expired, failed, rejected, withdrawn or superseded' },
  unknown:  { color: 'var(--ds-text-muted)', label: 'Status not stated', hint: 'The source did not publish a status' },
};
const IN_FORCE = ['active', 'passed', 'enacted', 'signed', 'effective', 'approved', 'in force', 'enforced', 'became law', 'public law', 'adopted'];
const PENDING = ['draft', 'pending', 'introduced', 'proposed', 'review', 'consideration'];
const INACTIVE = ['expired', 'repealed', 'failed', 'rejected', 'withdrawn', 'superseded', 'inactive'];

export function statusTone(status?: string | null): StatusTone {
  const s = (status || '').toLowerCase();
  if (!s) return 'unknown';
  if (INACTIVE.some(k => s.includes(k))) return 'inactive';       // "repealed after being enacted" is inactive
  if (IN_FORCE.some(k => s.includes(k))) return 'in_force';
  if (PENDING.some(k => s.includes(k))) return 'pending';
  return 'unknown';
}

// ── Choropleth (regulations per jurisdiction) ──────────────────────────────────
// Single monotonic ramp; the legend shows the numeric range of each step so
// "darker = more" is never a guess.
export const CHOROPLETH_RAMP = ['#c7d7fe', '#a4bcfd', '#8098f9', '#6172f3', '#444ce7', '#2d31a6'];
export const NO_DATA_COLOR = '#eaecf0';

export interface ChoroplethStep { color: string; from: number; to: number | null; label: string }

/** Log-spaced steps from 1 to `max`; the last step is open-ended. */
export function choroplethSteps(max: number): ChoroplethStep[] {
  const n = CHOROPLETH_RAMP.length;
  const top = Math.max(1, max);
  const edges: number[] = [1];
  for (let i = 1; i < n; i++) {
    const v = Math.round(Math.exp(Math.log(top + 1) * (i / n))) ;
    edges.push(Math.max(edges[i - 1] + 1, v));
  }
  return CHOROPLETH_RAMP.map((color, i) => {
    const from = edges[i];
    const to = i < n - 1 ? edges[i + 1] - 1 : null;
    const label = to === null ? `${from}+` : from === to ? `${from}` : `${from}–${to}`;
    return { color, from, to, label };
  });
}

export function choroplethColor(count: number, max: number): string {
  if (!count || count <= 0) return NO_DATA_COLOR;
  const steps = choroplethSteps(max);
  for (const s of steps) if (count >= s.from && (s.to === null || count <= s.to)) return s.color;
  return steps[steps.length - 1].color;
}

// ── Component ──────────────────────────────────────────────────────────────────
export interface LegendItem { color: string; label: string; hint?: string; shape?: 'square' | 'dot' | 'lock'; muted?: boolean }

export const Legend: React.FC<{ title?: string; items: LegendItem[]; compact?: boolean; dark?: boolean; style?: React.CSSProperties }> = ({ title, items, compact = false, dark = false, style }) => (
  <div role="list" aria-label={title || 'Legend'} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: compact ? 8 : 12, fontSize: compact ? 11 : 12, color: dark ? '#d0d5dd' : '#475467', ...style }}>
    {title && <span style={{ fontWeight: 700, color: dark ? '#eaecf0' : '#344054' }}>{title}</span>}
    {items.map(it => (
      <span key={it.label} role="listitem" title={it.hint} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, opacity: it.muted ? 0.7 : 1 }}>
        {it.shape === 'lock'
          ? <span aria-hidden style={{ fontSize: compact ? 10 : 12 }}>🔒</span>
          : <span aria-hidden style={{ width: compact ? 10 : 12, height: compact ? 10 : 12, borderRadius: it.shape === 'dot' ? '50%' : 3, background: it.color, border: '1px solid rgba(16,24,40,0.12)', flexShrink: 0 }} />}
        {it.label}
      </span>
    ))}
  </div>
);

export const statusLegendItems = (tones: StatusTone[] = ['in_force', 'pending', 'inactive', 'unknown']): LegendItem[] =>
  tones.map(t => ({ color: STATUS_TONES[t].color, label: STATUS_TONES[t].label, hint: STATUS_TONES[t].hint, shape: 'dot' }));

export default Legend;
